// @ts-nocheck
'use client'

import ActivityLoading from '@/components/activity-loading'

import React, { ReactElement, useEffect, useMemo, useState, useRef } from 'react'
import { EXPLORER_URL } from '@/utils/constants'
import useMempoolSales from '@/utils/hooks/useMempoolSales'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import { formatActivityUsd } from '@/utils/format-activity-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import InscriptionContent from '@/components/inscription-content'
import Link from 'next/link'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import Loading from '@/components/circle-loading'
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react'
import { useRouter } from 'next/navigation'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import isFungible from '@/utils/is-fungible'
import CollectionIcon from '@/components/collection-icon'
import numberWithCommas from '@/utils/format-number'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import VerifiedBadge from '@/components/verified-badge'
import getRelativeTime from '@/utils/get-relative-time'
import { SaleMarketplaceIcon, saleMarketplace } from '@/components/marketplace-icon'
import { MARKETPLACE_LABELS } from '@/utils/hooks/useSalesVolume'

// `sale.signals` mirrors `ord_sale_classifier::signals` bit for bit. Read it
// rather than re-deriving anything from the sighash string: the classifier is
// what decided, and these are its reasons.
const SIG_ALL_ACP = 0b0000_1000
const SIG_OW_PROTECTION_ESCROW = 0b0010_0000
const SIG_ORDNET_PASSTHROUGH = 0b0100_0000
const SIG_OW_PASSTHROUGH = 0b1000_0000
const SIG_ESCROWED = SIG_OW_PROTECTION_ESCROW | SIG_ORDNET_PASSTHROUGH | SIG_OW_PASSTHROUGH

/// The listing was sealed so it cannot be sniped out from under the buyer —
/// either signed ALL|ANYONECANPAY, or the asset is sitting in an escrow the
/// classifier recognised. For a row the classifier never saw, fall back to the
/// sighash string, which is all the ACP index gives us.
const isSealed = (item: any): boolean => {
	const signals = item?.sale?.signals
	if (typeof signals === 'number') return (signals & (SIG_ALL_ACP | SIG_ESCROWED)) !== 0
	return item?.mempool?.sighash === 'ALL|ANYONECANPAY'
}

const SALE_KIND_LABELS: Record<string, string> = {
	ask_fill: 'Listing filled',
	bid_fill: 'Offer accepted'
}

/// What the classifier concluded, in words. Null for a row it never saw.
const saleSummary = (item: any): string | null => {
	const sale = item?.sale
	if (!sale) return null
	const kind = SALE_KIND_LABELS[sale.kind] ?? 'Sale'
	const venue = MARKETPLACE_LABELS[String(sale.marketplace)]
	// Venue 0 is "we could not tell", not a venue. A Satflow Protection deposit
	// looks like this until its delivery transaction reveals the lockbox — ord
	// cannot name it at that moment either.
	if (!venue || sale.marketplace === 0) return `${kind} · venue unknown`
	return `${kind} · ${venue}`
}

interface TxGroup {
	txid: string
	sighash: string
	seen_at: number
	items: any[]
	totalSats: number
	/// Venue the API classified this transaction as, if it recognised one.
	/// Every item in a group shares it — they are the same transaction.
	marketplace: number | null
	/// Any item in the group being a sealed listing seals the group; they are
	/// one transaction, so one seller pre-signed them all the same way.
	sealed: boolean
}

const MempoolFeed: React.FC<{ hideHeader?: boolean; onSwitchTab?: () => void }> = ({
	hideHeader = false,
	onSwitchTab
}): ReactElement => {
	const router = useRouter()
	const { exchangeRate } = useExchangeRate()
	const hoveredItemRef = useRef<string | null>(null)
	const [expandedTxids, setExpandedTxids] = useState<Set<string>>(new Set())

	const { data, error, isLoading } = useMempoolSales()

	const items = useMemo(() => {
		if (!data) return []
		const arr = Array.isArray(data) ? data : data?.result || []
		return [...arr]
			// The API publishes two mempool indexes. A row carrying `sale` came
			// from the classifier: it is a sale by the same rules the confirmed
			// feed uses, so show it whatever its sighash — that is how secure
			// (0x81) fills finally appear here.
			//
			// A row without `sale` came from the raw ACP index, which only knows
			// that an ANYONECANPAY input was spent. It cannot tell a listing fill
			// from a bulk transfer and prices both as the committed output, so the
			// long-standing ALL|ANYONECANPAY filter still applies to those.
			.filter((item) => item.sale || item.mempool?.sighash !== 'ALL|ANYONECANPAY')
			.sort((a, b) => (b.mempool?.seen_at || 0) - (a.mempool?.seen_at || 0))
	}, [data])

	const groups = useMemo(() => {
		const groupMap = new Map<string, TxGroup>()
		for (const item of items) {
			const txid = item.mempool?.spending_txid
			if (!txid) continue
			if (!groupMap.has(txid)) {
				groupMap.set(txid, {
					txid,
					sighash: item.mempool?.sighash,
					seen_at: item.mempool?.seen_at,
					items: [],
					totalSats: 0,
					marketplace: saleMarketplace(item),
					sealed: isSealed(item),
					_seenOutpoints: new Set()
				})
			}
			const group = groupMap.get(txid)!
			group.items.push(item)
			// A rune row in the same transaction carries no venue, so take the
			// first one that does rather than whichever row happened to arrive
			// first.
			if (group.marketplace === null) group.marketplace = saleMarketplace(item)
			if (!group.sealed) group.sealed = isSealed(item)
			// Deduplicate price by outpoint — multiple inscriptions in the same
			// outpoint share the same satoshi_price + seller_address.
			const outpointKey = `${item.escrow?.satoshi_price}:${item.escrow?.seller_address}`
			if (!group._seenOutpoints.has(outpointKey)) {
				group._seenOutpoints.add(outpointKey)
				group.totalSats += item.escrow?.satoshi_price || 0
			}
		}
		return Array.from(groupMap.values())
	}, [items])

	const toggleExpand = (txid: string) => {
		setExpandedTxids((prev) => {
			const next = new Set(prev)
			if (next.has(txid)) next.delete(txid)
			else next.add(txid)
			return next
		})
	}

	const [, setTick] = useState(0)
	useEffect(() => {
		const hasRecent = items.some((item) => {
			const diff = Math.floor(Date.now() / 1000) - (item.mempool?.seen_at || 0)
			return diff < 60
		})
		if (!hasRecent) return
		const interval = setInterval(() => setTick((t) => t + 1), 2000)
		return () => clearInterval(interval)
	}, [items])

	const getItemKey = (item: any) => item.id || item.rune_id || item.meta?.name || 'unknown'

	const renderItemContent = (item: any) => {
		const isItemFungible = isFungible(item?.collection?.slug) || !!item?.rune_id
		const jsonAmount = item?.amount || item?.meta?.attributes?.[0]?.value
		const name =
			item.meta?.name ||
			item.collection?.name
				?.replace('TAP ', '')
				.replace('BRC20 ', '')
				.replace('DRC20 ', '')
				.replace('BEL20 ', '') ||
			item?.id?.slice(0, 8) ||
			'Unknown'

		const isRuneItem = !!item?.rune_id || isRune(item?.collection?.slug)
		const isImage = item?.content_type?.includes('image') || item?.collection?.slug === 'bitmap'
		const isText =
			!isImage && (item?.content_type?.includes('text') || item?.content_type?.includes('json'))

		return (
			<TooltipProvider delayDuration={0}>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="flex items-center gap-x-1 w-full">
							<div className="flex items-center min-w-[1rem] min-h-[1rem] size-4">
								{isRuneItem || isItemFungible ? (
									<CollectionIcon
										collection={item.collection}
										className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
										imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									/>
								) : item?.collection?.slug && !isImage ? (
									<CollectionIcon
										collection={item.collection}
										className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
										imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									/>
								) : isText ? (
									<div className="inline-flex items-center justify-center mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm bg-chrome-muted overflow-hidden">
										<div className="size-16 scale-[0.25] origin-center">
											<InscriptionContent
												className="size-16"
												inscription={item}
												isDetail={false}
												showPricePer={false}
												smallCard={true}
											/>
										</div>
									</div>
								) : (
									<InscriptionContent
										className="inline mr-1 size-4 min-h-[1rem] min-w-[1rem] rounded-sm"
										inscription={item}
										isDetail={false}
										showPricePer={true}
										smallCard={true}
									/>
								)}
							</div>

							<span className="text-xs truncate">
								{isItemFungible && jsonAmount && `${numberWithCommas(jsonAmount)} `}
								{name}
								<VerifiedBadge slug={item?.collection?.slug} />
							</span>
						</div>
					</TooltipTrigger>
					<TooltipContent
						side="left"
						className="flex flex-col p-3 bg-popover border border-border rounded-md shadow-md"
					>
						<div className="text-xs text-muted-foreground mb-2">
							{item?.meta?.name ||
								item.collection?.name ||
								(item.num ? `Inscription #${item.num}` : 'Unknown')}
						</div>
						<div className="size-[160px] sm:size-[256px] bg-card rounded-sm overflow-hidden">
							{isRuneItem || isAlkanes(item?.collection?.slug) ? (
								<CollectionIcon
									collection={item.collection}
									className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									imageClassName="h-full w-full min-w-[1rem] min-h-[1rem] rounded-sm"
								/>
							) : (
								<InscriptionContent
									className="size-full"
									inscription={item}
									isDetail={false}
									showPricePer={true}
									smallCard={true}
								/>
							)}
						</div>
						<div className="flex items-center justify-between mt-2 text-xs">
							<span className="text-muted-foreground">
								{saleSummary(item) ??
									(item.mempool?.sighash === 'SINGLE|ANYONECANPAY'
										? 'Sale'
										: item.mempool?.sighash)}
							</span>
							{item.escrow && (
								<span className="font-medium flex items-center">
									<BitcoinIcon className="inline mr-1 size-3" />
									{formatActivityBtc(item.escrow.satoshi_price)}
								</span>
							)}
						</div>
						<div className="flex items-center justify-between mt-1 text-xs">
							<div className="text-muted-foreground">
								{item.escrow?.seller_address && (
									<Link
										href={`/address/${item.escrow.seller_address}`}
										className="hover:text-primary"
										onClick={(e) => e.stopPropagation()}
									>
										{item.escrow.seller_address.slice(-5)}
									</Link>
								)}
								{item.escrow?.seller_address && item.escrow?.buyer_address && ' → '}
								{item.escrow?.buyer_address && (
									<Link
										href={`/address/${item.escrow.buyer_address}`}
										className="hover:text-primary"
										onClick={(e) => e.stopPropagation()}
									>
										{item.escrow.buyer_address.slice(-5)}
									</Link>
								)}
							</div>
							{item.escrow && exchangeRate > 0 && (
								<span className="text-muted-foreground">
									${formatActivityUsd((item.escrow.satoshi_price / 1e8) * exchangeRate)}
								</span>
							)}
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	const handleRowClick = (item: any) => {
		if (item.collection?.slug) {
			router.push(`/collection/${item.collection.slug}`)
		}
	}

	const renderSingleRow = (item: any, group: TxGroup, key: string, showLine?: boolean) => (
		<TableRow
			key={key}
			className="whitespace-nowrap border-0 hover:bg-accent/50 cursor-pointer transition-all my-px"
			onClick={() => handleRowClick(item)}
			onMouseEnter={() => (hoveredItemRef.current = getItemKey(item))}
			onMouseLeave={() => (hoveredItemRef.current = null)}
		>
			<TableCell className="w-[40px] py-0 pl-3 text-xs text-muted-foreground text-left relative">
				{showLine ? (
					<div className="absolute left-[22px] top-0 bottom-0 flex items-center">
						<div className="w-px h-full bg-muted-foreground/30" />
					</div>
				) : (
					<Link
						href={`${EXPLORER_URL}/tx/${group.txid}`}
						target="_blank"
						onClick={(e) => e.stopPropagation()}
						className="hover:text-primary flex items-center gap-0.5 py-1"
					>
						{getRelativeTime(group.seen_at)}
					</Link>
				)}
			</TableCell>
			<TableCell className="w-[50%] sm:w-[40%] p-1 text-xs text-left overflow-hidden">
				{renderItemContent(item)}
			</TableCell>
			<TableCell className="py-1 pr-3 sm:pr-4 pl-1 text-xs text-right">
				<div className="flex items-center justify-end gap-0.5 overflow-hidden">
					<span className="shrink-0 inline-flex">
						<Loading size={10} className="!w-2.5 !h-2.5" />
					</span>
					<SaleMarketplaceIcon sale={item} />
					{item.sale?.kind === 'bid_fill' && (
						<span
							title="Offer accepted — the seller signed the whole transaction"
							className="shrink-0 rounded-[3px] border border-border px-1 text-[9px] leading-[13px] text-muted-foreground"
						>
							offer
						</span>
					)}
					{isSealed(item) && (
						<ShieldCheck
							className="shrink-0 size-3 text-yellow-500"
							aria-label="Sealed listing"
						/>
					)}
					<BitcoinIcon className="shrink-0 size-3 relative -top-[0.5px]" />
					<span className="truncate">
						{formatActivityBtc(item.escrow?.satoshi_price || 0)}
						{exchangeRate > 0 && (
							<span className="text-muted-foreground ml-0.5 text-[10px]">
								(${formatActivityUsd((item.escrow?.satoshi_price / 1e8) * exchangeRate)}
								)
							</span>
						)}
					</span>
				</div>
			</TableCell>
		</TableRow>
	)

	const renderGroup = (group: TxGroup) => {
		const isMulti = group.items.length > 1
		const isExpanded = expandedTxids.has(group.txid)

		if (!isMulti) {
			return renderSingleRow(group.items[0], group, `single-${group.txid}`)
		}

		return (
			<React.Fragment key={`group-${group.txid}`}>
				<TableRow
					className="whitespace-nowrap border-0 hover:bg-accent/50 cursor-pointer transition-all my-px"
					onClick={() => toggleExpand(group.txid)}
				>
					<TableCell className="w-[40px] py-0 pl-3 text-xs text-muted-foreground text-left">
						<Link
							href={`${EXPLORER_URL}/tx/${group.txid}`}
							target="_blank"
							onClick={(e) => e.stopPropagation()}
							className="hover:text-primary flex items-center gap-0.5 py-1"
						>
							{getRelativeTime(group.seen_at)}
						</Link>
					</TableCell>
					<TableCell className="w-[50%] sm:w-[40%] p-1 text-xs text-left overflow-hidden">
						<div className="flex items-center gap-1">
							<span className="truncate">{renderItemContent(group.items[0])}</span>
							<span className="shrink-0 text-muted-foreground">+{group.items.length - 1}</span>
							{isExpanded ? (
								<ChevronDown className="size-3 text-muted-foreground flex-shrink-0 ml-[2px]" />
							) : (
								<ChevronUp className="size-3 text-muted-foreground flex-shrink-0 ml-[2px]" />
							)}
						</div>
					</TableCell>
					<TableCell className="py-1 pr-3 sm:pr-4 pl-1 text-xs text-right">
						<div className="flex items-center justify-end gap-0.5 overflow-hidden">
								<span className="shrink-0 inline-flex">
								<Loading size={10} className="!w-2.5 !h-2.5" />
							</span>
							{group.marketplace !== null && (
								<SaleMarketplaceIcon sale={{ marketplace: group.marketplace }} />
							)}
							{group.sealed && (
								<ShieldCheck
									className="shrink-0 size-3 text-yellow-500"
									aria-label="Sealed listing"
								/>
							)}
							<BitcoinIcon className="shrink-0 size-3 relative -top-[0.5px]" />
							<span className="truncate">
								{formatActivityBtc(group.totalSats)}
								{exchangeRate > 0 && (
									<span className="text-muted-foreground ml-0.5 text-[10px]">
										(${formatActivityUsd((group.totalSats / 1e8) * exchangeRate)})
									</span>
								)}
							</span>
						</div>
					</TableCell>
				</TableRow>
				{isExpanded &&
					group.items.map((item, i) =>
						renderSingleRow(item, group, `${group.txid}-${getItemKey(item)}-${i}`, true)
					)}
			</React.Fragment>
		)
	}

	return (
		<div className="flex flex-col h-full bg-transparent">
			{!hideHeader && (
				<div className="z-[1] px-4 py-3 border-b border-border">
					<h1 className="text-xs font-medium text-foreground -ml-1">
						Mempool Activity
						{items.length > 0 && (
							<span className="text-muted-foreground ml-1 font-normal">{items.length}</span>
						)}
					</h1>
				</div>
			)}
			<div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
				{isLoading && (
					<ActivityLoading />
				)}
				{error && (
					<div className="flex justify-center items-center py-8 text-sm text-muted-foreground">
						Unable to load mempool data
					</div>
				)}
				{!isLoading && !error && items.length === 0 && (
					<div className="flex flex-col justify-center items-center py-8 gap-3 text-sm text-muted-foreground">
						No mempool activity
						{onSwitchTab && (
							<button
								onClick={onSwitchTab}
								className="px-3 py-1.5 text-xs border border-border rounded text-muted-foreground hover:bg-white/5 transition-colors"
							>
								Switch Tab
							</button>
						)}
					</div>
				)}
				{groups.length > 0 && (
					<Table className="w-full table-fixed">
						<TableBody>{groups.map(renderGroup)}</TableBody>
					</Table>
				)}
			</div>
		</div>
	)
}

export default MempoolFeed
